import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import Layout from '../../components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import FormFieldText from '../../components/FormComponents/FormFieldText';
import FormFieldSwitch from '../../components/FormComponents/FormFieldSwitch';
import { Church } from '../../types';

const Schema = Yup.object().shape({
  name: Yup.string().max(255).required('Required'),
  phoneNumber: Yup.string().max(255).nullable(),
  responsiblePersonName: Yup.string().max(255).nullable(),
  streetAddress: Yup.string().max(255).nullable(),
  postalCode: Yup.string().max(255).nullable(),
  city: Yup.string().max(255).nullable(),
  regionOrLocation: Yup.string().max(255).nullable(),
  sizeInfo: Yup.string().nullable(),
});

const ChurchEditScreen = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    churchStore: { selectedChurch, fetchChurch, clearSelected, saveChurch },
  } = useMst();

  useEffect(() => {
    if (id && id !== 'create') {
      fetchChurch(id);
    } else {
      clearSelected();
    }
  }, [id, fetchChurch, clearSelected]);

  const initialValues: Church = selectedChurch ?? {
    name: '',
    isActive: true,
  };

  const onSubmit = async (values: Church, formik: FormikHelpers<Church>) => {
    await saveChurch({ ...values, id: values.id ?? (id !== 'create' ? id : undefined) });
    formik.setSubmitting(false);
    navigate('/churches');
  };

  return (
    <Layout title={t('church.edit')}>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={Schema} onSubmit={onSubmit}>
        {({ isSubmitting, isValid }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormFieldText name="name" label={t('church.name') ?? 'Name'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormFieldText name="phoneNumber" label={t('church.phoneNumber') ?? 'Phone'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormFieldText
                  name="responsiblePersonName"
                  label={t('church.responsiblePersonName') ?? 'Responsible'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormFieldText name="streetAddress" label={t('church.streetAddress') ?? 'Street'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="postalCode" label={t('church.postalCode') ?? 'Postal code'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="city" label={t('church.city') ?? 'City'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormFieldText name="regionOrLocation" label={t('church.regionOrLocation') ?? 'Region/Location'} />
              </Grid>
              <Grid item xs={12}>
                <FormFieldText name="sizeInfo" label={t('church.sizeInfo') ?? 'Size info'} />
              </Grid>
              <Grid item xs={12}>
                <FormFieldSwitch name="isActive" label={t('active') ?? 'Active'} />
              </Grid>
              <Grid item xs={12} display="flex" gap={1}>
                <Button type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                  {t('save') ?? 'Save'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/churches')}>
                  {t('cancel') ?? 'Cancel'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Layout>
  );
});

export default ChurchEditScreen;
